import { BaseEnvContribution, RafBasedSTO } from "@visactor/vrender-core";

import { CanvasWrapEnableWH } from "./canvas-wrap";

const HARMONY_CANVAS_BRIDGE_ERROR = "Harmony canvas bridge is unavailable. VRender Harmony env requires envParams.canvasFactory or a Harmony runtime that exposes a global createCanvas/createOffscreenCanvas capability.", HARMONY_CANVAS_SIZE_ERROR = "Harmony canvas size is unavailable. Pass stage width/height when creating a Harmony stage canvas.";

function isValidCoordinate(value) {
    return "number" == typeof value && Number.isFinite(value);
}

function pickCoordinate(...values) {
    for (let i = 0; i < values.length; i++) if (isValidCoordinate(values[i])) return values[i];
}

function getPrimaryTouch(event) {
    var _a, _b, _c;
    return null !== (_b = null === (_a = null == event ? void 0 : event.changedTouches) || void 0 === _a ? void 0 : _a[0]) && void 0 !== _b ? _b : null === (_c = null == event ? void 0 : event.touches) || void 0 === _c ? void 0 : _c[0];
}

function getHarmonyEventPoint(event) {
    const touch = getPrimaryTouch(event), x = pickCoordinate(null == event ? void 0 : event.x, null == event ? void 0 : event.offsetX, null == event ? void 0 : event.clientX, null == event ? void 0 : event.pageX, null == touch ? void 0 : touch.x, null == touch ? void 0 : touch.offsetX, null == touch ? void 0 : touch.clientX, null == touch ? void 0 : touch.pageX), y = pickCoordinate(null == event ? void 0 : event.y, null == event ? void 0 : event.offsetY, null == event ? void 0 : event.clientY, null == event ? void 0 : event.pageY, null == touch ? void 0 : touch.y, null == touch ? void 0 : touch.offsetY, null == touch ? void 0 : touch.clientY, null == touch ? void 0 : touch.pageY);
    return null == x || null == y ? null : {
        x: x,
        y: y
    };
}

function getHarmonyRuntime(params) {
    var _a;
    return null !== (_a = null == params ? void 0 : params.harmony) && void 0 !== _a ? _a : null == params ? void 0 : params.runtime;
}

function getHarmonyPixelRatio(params) {
    var _a;
    return null !== (_a = null == params ? void 0 : params.pixelRatio) && void 0 !== _a ? _a : 1;
}

function getCanvasSize(width, height) {
    if (!isValidCoordinate(width) || !isValidCoordinate(height)) throw new Error(HARMONY_CANVAS_SIZE_ERROR);
    return {
        width: width,
        height: height
    };
}

function getGlobalOffscreenCanvasCtor() {
    try {
        return "undefined" != typeof OffscreenCanvas ? OffscreenCanvas : void 0;
    } catch (err) {
        return;
    }
}

function createFallbackOffscreenCanvas(width, height) {
    const OffscreenCanvasCtor = getGlobalOffscreenCanvasCtor();
    return "function" != typeof OffscreenCanvasCtor ? null : new OffscreenCanvasCtor(width, height);
}

function wrapHarmonyNativeCanvas(nativeCanvas, id, width, height, dpr) {
    var _a, _b, _c;
    nativeCanvas.width = width * dpr, nativeCanvas.height = height * dpr;
    const context = null !== (_b = null === (_a = nativeCanvas.getContext) || void 0 === _a ? void 0 : _a.call(nativeCanvas, "2d")) && void 0 !== _b ? _b : null === (_c = nativeCanvas.getContext) || void 0 === _c ? void 0 : _c.call(nativeCanvas);
    if (!context) throw new Error(HARMONY_CANVAS_BRIDGE_ERROR);
    return "function" != typeof nativeCanvas.getBoundingClientRect && (nativeCanvas.getBoundingClientRect = () => ({
        width: width,
        height: height
    })), new CanvasWrapEnableWH(nativeCanvas, context, dpr, width, height, id);
}

function createHarmonyNativeCanvas(id, width, height, dpr, offscreen, params, runtime) {
    const options = {
        id: id,
        width: width,
        height: height,
        dpr: dpr,
        offscreen: offscreen
    };
    if (params.canvasFactory) return params.canvasFactory(options);
    if (offscreen && "function" == typeof (null == runtime ? void 0 : runtime.createOffscreenCanvas)) {
        const canvas = runtime.createOffscreenCanvas(options);
        if (canvas) return canvas;
    }
    if (!offscreen && "function" == typeof (null == runtime ? void 0 : runtime.createCanvas)) {
        const canvas = runtime.createCanvas(options);
        if (canvas) return canvas;
    }
    if (offscreen) {
        const canvas = createFallbackOffscreenCanvas(width * dpr, height * dpr);
        if (canvas) return canvas;
    }
    throw new Error(HARMONY_CANVAS_BRIDGE_ERROR);
}

export class HarmonyEnvContribution extends BaseEnvContribution {
    constructor() {
        super(), this.type = "harmony", this.supportEvent = !0, this.canvasMap = new Map, 
        this.freeCanvasList = [], this.canvasIdx = 0, this.supportsTouchEvents = !0;
        try {
            this.supportsPointerEvents = !!globalThis.PointerEvent, this.supportsMouseEvents = !!globalThis.MouseEvent;
        } catch (err) {
            this.supportsPointerEvents = !1, this.supportsMouseEvents = !1;
        }
        this.applyStyles = !0, this.rafSTO = new RafBasedSTO(0);
    }
    configure(service, params = {}) {
        service.env === this.type && (service.setActiveEnvContribution(this), this.harmonyEnvParams = params, 
        this.harmonyRuntime = getHarmonyRuntime(params));
    }
    getDynamicCanvasCount() {
        return 9999;
    }
    getStaticCanvasCount() {
        return 9999;
    }
    loadImage(url) {
        var _a;
        if ("function" != typeof (null === (_a = this.harmonyRuntime) || void 0 === _a ? void 0 : _a.createImage)) return Promise.resolve({
            data: null,
            loadState: "fail"
        });
        const img = this.harmonyRuntime.createImage(url);
        return new Promise((resolve => {
            img.onload = () => {
                resolve({
                    data: img,
                    loadState: "success"
                });
            }, img.onerror = () => {
                resolve({
                    data: null,
                    loadState: "fail"
                });
            };
        }));
    }
    loadSvg(url) {
        return Promise.resolve({
            data: null,
            loadState: "fail"
        });
    }
    createCanvas(params) {
        var _a, _b;
        const envParams = null !== (_a = this.harmonyEnvParams) && void 0 !== _a ? _a : {}, runtime = getHarmonyRuntime(envParams), dpr = null !== (_b = params.dpr) && void 0 !== _b ? _b : getHarmonyPixelRatio(envParams), size = getCanvasSize(params.width, params.height);
        if (null != params.id) {
            const id = String(params.id), existing = this.canvasMap.get(id);
            if (existing) return existing;
            const canvas = wrapHarmonyNativeCanvas(createHarmonyNativeCanvas(id, size.width, size.height, dpr, !1, envParams, runtime), id, size.width, size.height, dpr);
            return this.canvasMap.set(id, canvas), canvas;
        }
        const result = this.freeCanvasList[this.canvasIdx] || this.freeCanvasList[this.freeCanvasList.length - 1];
        if (this.canvasIdx++, result) return result;
        const id = Math.random().toString(), canvas = wrapHarmonyNativeCanvas(createHarmonyNativeCanvas(id, size.width, size.height, dpr, !0, envParams, runtime), id, size.width, size.height, dpr);
        return this.canvasMap.set(id, canvas), this.freeCanvasList.push(canvas), canvas;
    }
    createOffscreenCanvas(params) {}
    releaseCanvas(canvas) {}
    getDevicePixelRatio() {
        return getHarmonyPixelRatio(this.harmonyEnvParams);
    }
    getRequestAnimationFrame() {
        return callback => this.rafSTO.call(callback);
    }
    getCancelAnimationFrame() {
        return h => {
            this.rafSTO.clear(h);
        };
    }
    mapToCanvasPoint(event) {
        var _a;
        return null !== (_a = getHarmonyEventPoint(event)) && void 0 !== _a ? _a : event;
    }
    addEventListener(type, listener, options) {
        return null;
    }
    removeEventListener(type, listener, options) {
        return null;
    }
    dispatchEvent(event) {
        return null;
    }
    getElementById(str) {
        var _a;
        return null !== (_a = this.canvasMap.get(str)) && void 0 !== _a ? _a : null;
    }
    getRootElement() {
        return null;
    }
    getDocument() {
        return null;
    }
    release(...params) {}
}
//# sourceMappingURL=harmony-contribution.js.map
